import { QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useHttpClient } from '@bonusly/http-client';
import { useCurrentCompany } from '@bonusly/who-am-i';

const headers = { 'Key-Inflection': 'camel' };

// Mirrors the backend `phase` enum. `pre_expiry` while the trial is still
// running; `post_expiry` once it has ended (but recently enough that we still
// want to nudge the admin to pick a plan).
export type TrialWhatsNextPhase = 'pre_expiry' | 'post_expiry';

// Milestone the backend computed for the auto-present gate. `null` means no
// milestone is active. The backend owns the `milestone-<name>` dismissal naming
// (see `useDismissTrialWhatsNext`); the client only uses this for analytics.
export type TrialWhatsNextMilestone = '7' | '3' | '1' | 'post-expiry' | null;

export interface TrialWhatsNextUsage {
	recognitionsCount: number;
	participantsCount: number;
	// Number of days since the trial started; drives the dynamic "in the last N
	// days" window in the usage sentence (replaces the previously hard-coded 30).
	windowDays: number;
}

export interface TrialWhatsNextFreeOption {
	eligible: boolean;
	// Disabled-card copy when the company can't switch to Free (e.g. too many
	// seats). `null` when eligible.
	reason: string | null;
}

export interface TrialWhatsNextPayload {
	phase: TrialWhatsNextPhase;
	// Integer; `0` once the trial has expired.
	daysLeft: number;
	usage: TrialWhatsNextUsage;
	freeOption: TrialWhatsNextFreeOption;
	milestone: TrialWhatsNextMilestone;
	// The authoritative server-side decision for whether to auto-present the
	// interstitial on login. The client-side trial gate is only a cheap
	// pre-filter; this is the source of truth.
	shouldAutoPresent: boolean;
}

// Scoped to who-am-i company identity and subscription state so a stale
// in-trial payload is not served after trial conversion or other status
// changes within the same SPA session.
export type TrialWhatsNextQueryScope = {
	companyId: string | undefined;
	companyStatus: string | undefined;
	trialEndDate: string | undefined;
};

export const trialWhatsNextQueryScopeFromCompany = (
	company: { id?: string; status?: string; trial_end_date?: string } | null | undefined
): TrialWhatsNextQueryScope => ({
	companyId: company?.id,
	companyStatus: company?.status,
	trialEndDate: company?.trial_end_date,
});

export const trialWhatsNextQueryKey = (
	scope: TrialWhatsNextQueryScope = {
		companyId: undefined,
		companyStatus: undefined,
		trialEndDate: undefined,
	}
) => ['trial-whats-next', scope.companyId, scope.companyStatus, scope.trialEndDate] as const;

export const suppressTrialWhatsNextAutoPresent = (queryClient: QueryClient, scope: TrialWhatsNextQueryScope) => {
	queryClient.setQueryData<TrialWhatsNextPayload | null>(trialWhatsNextQueryKey(scope), (old) => (old ? { ...old, shouldAutoPresent: false } : old));
};

export const trialWhatsNextPath = (prefix = '') => `${prefix}admin/trial/whats_next`;

export const trialWhatsNextDismissPath = (prefix = '') => `${prefix}admin/trial/whats_next/dismiss`;

/**
 * Dismisses the current trial "what's next?" milestone (RR-75). The backend
 * recomputes the active milestone server-side and idempotently records the
 * dismissal, so the client no longer needs to know the `milestone-<name>`
 * format or which milestone is active — it just asks to dismiss "the current
 * one". Both the auto-presented interstitial and the standalone route use this
 * single mutation, replacing the per-surface dismissal wiring.
 *
 * On success we also patch the in-session cache so the login interstitial stays
 * closed for the rest of the session: the backend reads dismissals from a
 * replica, so an immediate refetch could otherwise still report
 * `shouldAutoPresent: true` until replication catches up.
 */
export const useDismissTrialWhatsNext = () => {
	const http = useHttpClient('v2');
	const queryClient = useQueryClient();
	const company = useCurrentCompany();
	const scope = trialWhatsNextQueryScopeFromCompany(company);

	return useMutation({
		mutationFn: async (): Promise<void> => {
			await http.post(trialWhatsNextDismissPath(), {});
		},
		onSuccess: () => suppressTrialWhatsNextAutoPresent(queryClient, scope),
	});
};

/**
 * Reads the "end of trial: what's next?" recommendation (RR-75). The endpoint
 * is admin-gated (finance / global admins) on the backend. Returns 404 when
 * the company is neither in trial nor recently expired, and 403 when the caller
 * lacks finance/global permission — we map both to `null` so callers simply
 * render nothing, mirroring the `useBizyBillingSettings` convention.
 */
export const useTrialWhatsNext = (options?: { enabled?: boolean }) => {
	const http = useHttpClient('v2');
	const company = useCurrentCompany();
	const scope = trialWhatsNextQueryScopeFromCompany(company);
	const isEnabled = options?.enabled ?? true;

	return useQuery({
		queryKey: trialWhatsNextQueryKey(scope),
		queryFn: async (): Promise<TrialWhatsNextPayload | null> => {
			try {
				const response = await http.get<TrialWhatsNextPayload>(trialWhatsNextPath(), { headers });
				return response.data;
			} catch (error: unknown) {
				const httpError = error as { response?: { status?: number } };
				const status = httpError?.response?.status;
				if (status === 404 || status === 403) {
					return null;
				}
				throw error;
			}
		},
		enabled: isEnabled,
		refetchOnWindowFocus: false,
		// Usage counts and plan eligibility are stable within a session. Timing
		// copy (days left, pre/post-expiry) is derived client-side via
		// `useTrialStatus`, not from cached `daysLeft` / `phase`.
		staleTime: 5 * 60 * 1000,
	});
};
